import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getTransform } from "../../../../../utils/getTransform";
import { TOOL_PREP_PREVIEW_BG } from "../../../../../utils/editorEntrySession";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
} from "../../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import {
  getWatermarkFontStack,
  watermarkEffectStyle,
  watermarkFontCssStyle,
  watermarkTextDecoration,
  watermarkTextAdvancedCss,
  watermarkTextBoxLayout,
  watermarkTilePositions,
  watermarkRotationPreservingCenter,
  resolveSingleMarkPosition,
  syncMarginFromNearestEdge,
  WATERMARK_CONTENT_TYPES,
} from "../../../watermark/Popup/watermarkSettings";
import { watermarkLogoMarkStyle } from "../../../watermark/Editor/watermarkImageFormatAdapter";
import WatermarkLogoMarkMedia from "../../../watermark/Popup/WatermarkLogoMarkMedia";
import {
  listSignPdfPlacementsForPage,
  listSignPdfPlacementsByBlockId,
  patchActiveSignPdfPlacement,
  patchSignPdfBlockGeometry,
  removeSignPdfPlacementOrBlock,
  resolveSignPdfPlacementToWatermark,
  scaleSignPdfBlockFromSnapshot,
  setActiveSignPdfPlacement,
  snapshotSignPdfBlockForResize,
  SIGN_PDF_BLOCK_MIME,
  SIGN_PDF_DEFAULT_TEXT_LABEL,
  SIGN_PDF_FIELD_KINDS,
  updateSignPdfPlacement,
} from "./signPdfPlacements";
import {
  measureSignPdfText,
  resolveSignPdfFontSizePx,
  resolveSignPdfLogoDisplaySize,
  signPdfFontSizeFieldsFromPx,
} from "./signPdfSettings";
import "../../../object/shared/Popup/TransformPreview.css";
import "../../../watermark/Popup/WatermarkPreview.css";
import "./SignPdfPreview.css";

const HANDLES = ["nw", "ne", "sw", "se"];
const BLOCK_BOUNDS_PAD_PX = 10;
const KEY_NUDGE_PX = 1;
const KEY_NUDGE_SHIFT_PX = 10;
const SNAP_GUIDE_FLASH_MS = 700;

const KEYBOARD_MODAL_SELECTORS = [
  ".sp-finish-root",
  ".sp-finish-thanks-root",
  ".sp-intent-root",
  ".sp-who-root",
  ".spsm-root",
  ".tool-prep-popup__confirm",
].join(",");

const KEYBOARD_NUDGE_BLOCK_SELECTORS = [
  ".sp-dd__menu",
  ".sp-fields-menu",
  ".sp-blocks-panel--flyout",
  ".sp-finish-popdown",
  ".sp-page-popdown",
  ".sp-review-popdown",
  KEYBOARD_MODAL_SELECTORS,
].join(",");

function isTypingTarget(el) {
  if (!(el instanceof Element)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(el.isContentEditable);
}

/** Block arrow-key nudge while typing or in menus / modals. */
function blocksKeyboardNudge(el) {
  if (isTypingTarget(el)) return true;
  return Boolean(el.closest(KEYBOARD_NUDGE_BLOCK_SELECTORS));
}

/** Block Delete on field only while typing or in full modals — not toolbar dropdowns. */
function blocksKeyboardDelete(el) {
  if (isTypingTarget(el)) return true;
  return Boolean(el.closest(KEYBOARD_MODAL_SELECTORS));
}

/** Axis-aligned bounds (px) covering all block member layouts. */
function computeBlockBoundsPx(entries, stageW, stageH, pad = BLOCK_BOUNDS_PAD_PX) {
  if (!entries?.length || !stageW || !stageH) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const entry of entries) {
    const layout = layoutForWatermark(
      entry.watermark,
      stageW,
      stageH
    );
    if (!layout?.pos || !layout.metrics) continue;
    const { pos, metrics, rotation } = layout;
    const hw = Math.max(1, metrics.width) / 2;
    const hh = Math.max(1, metrics.height) / 2;
    const rad = ((Number(rotation) || 0) * Math.PI) / 180;
    const c = Math.abs(Math.cos(rad));
    const s = Math.abs(Math.sin(rad));
    const rw = hw * c + hh * s;
    const rh = hw * s + hh * c;
    const cx = pos.x * stageW;
    const cy = pos.y * stageH;
    minX = Math.min(minX, cx - rw);
    maxX = Math.max(maxX, cx + rw);
    minY = Math.min(minY, cy - rh);
    maxY = Math.max(maxY, cy + rh);
  }
  if (!Number.isFinite(minX)) return null;
  return {
    left: minX - pad,
    top: minY - pad,
    width: Math.max(4, maxX - minX + pad * 2),
    height: Math.max(4, maxY - minY + pad * 2),
  };
}

function isLogoWatermark(wm) {
  return (
    wm?.contentType === WATERMARK_CONTENT_TYPES.LOGO && Boolean(wm?.logoUrl)
  );
}

function layoutForWatermark(wm, stageW, stageH) {
  if (!wm || !stageW || !stageH) return null;
  const logo = isLogoWatermark(wm);
  const rotation = Number(wm.rotation) || 0;
  const fontFamily = getWatermarkFontStack(wm.fontId || wm.fontFamily);
  const fontSizePx = resolveSignPdfFontSizePx(wm, stageW, stageH);
  const logoSize = logo
    ? resolveSignPdfLogoDisplaySize(
        wm,
        stageW,
        stageH,
        wm.logoNaturalWidth,
        wm.logoNaturalHeight,
        wm.logoCropRect
      )
    : { width: 0, height: 0 };
  const textMetrics = logo
    ? { width: 0, height: 0 }
    : measureSignPdfText(
        wm.text?.trim() ? wm.text : " ",
        fontSizePx,
        fontFamily,
        wm.fontWeight,
        {
          letterSpacing: wm.letterSpacing,
          fontStyle: wm.fontStyle,
        }
      );
  const metrics = logo
    ? { width: logoSize.width, height: logoSize.height }
    : textMetrics;
  if (!metrics || !Number(metrics.width) || !Number(metrics.height)) {
    return null;
  }
  const hasContent = logo ? Boolean(wm.logoUrl) : true;
  if (!hasContent) return null;

  const positions = watermarkTilePositions({
    tile: "single",
    spacing: 0,
    stageWidth: stageW,
    stageHeight: stageH,
    textWidth: metrics.width,
    textHeight: metrics.height,
    fontSizePx: logo
      ? Math.max(metrics.width, metrics.height)
      : fontSizePx,
    rotationDeg: rotation,
    position: wm.position || "center",
    margin: wm.margin,
    offsetX: Number(wm.offsetX) || 0,
    offsetY: Number(wm.offsetY) || 0,
    flushEdges: wm.flushEdges !== false,
  });
  const pos = positions[0];
  if (!pos) return null;
  return {
    pos,
    metrics,
    logo,
    rotation,
    fontFamily,
    fontSizePx,
  };
}

const SNAP_THRESHOLD_PX = 8;
const ROW_GAP_PX = 10;

function rotatedHalfExtents(metrics, rotationDeg = 0) {
  const hw = Math.max(1, metrics.width) / 2;
  const hh = Math.max(1, metrics.height) / 2;
  const rad = ((Number(rotationDeg) || 0) * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  return {
    halfW: hw * c + hh * s,
    halfH: hw * s + hh * c,
  };
}

function boundsFromCenter(centerX, centerY, halfW, halfH, stageW, stageH) {
  const cx = centerX * stageW;
  const cy = centerY * stageH;
  return {
    left: cx - halfW,
    right: cx + halfW,
    top: cy - halfH,
    bottom: cy + halfH,
    cx,
    cy,
  };
}

/**
 * Snap dragged mark by edges + centers — left/right/top/bottom, row below/above, page edges.
 */
function snapPlacementWithGuides(
  centerX,
  centerY,
  halfW,
  halfH,
  stageW,
  stageH,
  otherBounds = []
) {
  if (!stageW || !stageH) {
    return { centerX, centerY, guides: null };
  }

  const drag = boundsFromCenter(centerX, centerY, halfW, halfH, stageW, stageH);
  const page = {
    left: 0,
    right: stageW,
    top: 0,
    bottom: stageH,
    cx: stageW / 2,
    cy: stageH / 2,
  };
  const targets = [...otherBounds, page];

  let nextCenterX = centerX;
  let nextCenterY = centerY;
  let guideV = null;
  let guideH = null;
  let bestDx = SNAP_THRESHOLD_PX + 1;
  let bestDy = SNAP_THRESHOLD_PX + 1;

  for (const t of targets) {
    const xCandidates = [
      { dragEdge: drag.left, edge: t.left, newCx: t.left + halfW, guide: t.left },
      { dragEdge: drag.right, edge: t.right, newCx: t.right - halfW, guide: t.right },
      { dragEdge: drag.cx, edge: t.cx, newCx: t.cx, guide: t.cx },
    ];
    for (const snap of xCandidates) {
      const d = Math.abs(snap.dragEdge - snap.edge);
      if (d <= SNAP_THRESHOLD_PX && d < bestDx) {
        bestDx = d;
        nextCenterX = snap.newCx / stageW;
        guideV = snap.guide / stageW;
      }
    }

    const yCandidates = [
      { dragEdge: drag.top, edge: t.top, newCy: t.top + halfH, guide: t.top },
      {
        dragEdge: drag.bottom,
        edge: t.bottom,
        newCy: t.bottom - halfH,
        guide: t.bottom,
      },
      {
        dragEdge: drag.top,
        edge: t.bottom + ROW_GAP_PX,
        newCy: t.bottom + ROW_GAP_PX + halfH,
        guide: t.bottom + ROW_GAP_PX,
      },
      {
        dragEdge: drag.bottom,
        edge: t.top - ROW_GAP_PX,
        newCy: t.top - ROW_GAP_PX - halfH,
        guide: t.top - ROW_GAP_PX,
      },
      { dragEdge: drag.cy, edge: t.cy, newCy: t.cy, guide: t.cy },
    ];
    for (const snap of yCandidates) {
      const d = Math.abs(snap.dragEdge - snap.edge);
      if (d <= SNAP_THRESHOLD_PX && d < bestDy) {
        bestDy = d;
        nextCenterY = snap.newCy / stageH;
        guideH = snap.guide / stageH;
      }
    }
  }

  const guides =
    guideV != null || guideH != null ? { v: guideV, h: guideH } : null;

  return {
    centerX: guideV != null ? nextCenterX : centerX,
    centerY: guideH != null ? nextCenterY : centerY,
    guides,
  };
}

/**
 * Sign PDF canvas — first-class multi-placement (Phase B).
 * All page placements render; one active gets move/resize/rotate.
 */
const SignPdfPreview = ({
  imageUrl = null,
  loading = false,
  loadingLabel = "Loading preview…",
  emptyLabel = "Upload a PDF to preview",
  previewBg = TOOL_PREP_PREVIEW_BG.ORIGINAL,
  session = null,
  currentPage = 1,
  onSessionChange = null,
  compareMode = "preview",
  stageInset = TOOL_PREP_STAGE_INSET,
  onFieldDrop = null,
  onSigningBlockDrop = null,
  onPlacementDragChange = null,
  onStageSizeChange = null,
}) => {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const [snapGuides, setSnapGuides] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const sessionRef = useRef(session);
  const snapGuideTimerRef = useRef(null);
  const editInputRef = useRef(null);
  const editDraftRef = useRef("");
  const editingTextIdRef = useRef(null);
  const editReadyRef = useRef(false);
  const editFocusTokenRef = useRef(0);
  sessionRef.current = session;
  const pageRef = useRef(currentPage);
  pageRef.current = currentPage;
  const resolvedMarksRef = useRef([]);
  const stageSizeRef = useRef(stageSize);
  stageSizeRef.current = stageSize;

  editingTextIdRef.current = editingTextId;
  editDraftRef.current = editDraft;

  const showMarks = compareMode !== "original";
  const textEditing = editingTextId != null;

  const placements = useMemo(
    () => listSignPdfPlacementsForPage(session, currentPage),
    [session, currentPage]
  );

  const activeId = session?.activePlacementId || null;

  const resolvedMarks = useMemo(() => {
    return placements
      .map((p) => {
        const wm = resolveSignPdfPlacementToWatermark(session, p);
        if (!wm) return null;
        return { placement: p, watermark: wm };
      })
      .filter(Boolean);
  }, [placements, session]);
  resolvedMarksRef.current = resolvedMarks;

  // Only a real activePlacementId is selected — no fallback to first mark.
  const activeEntry =
    (activeId &&
      resolvedMarks.find((m) => m.placement.id === activeId)) ||
    null;
  const activeWm = activeEntry?.watermark || null;
  const activeLayout = useMemo(
    () => layoutForWatermark(activeWm, stageSize.width, stageSize.height),
    [activeWm, stageSize.width, stageSize.height]
  );

  // Local (unrotated) metrics only — selection chrome is CSS-rotated with the mark.
  // Never use getClientRects()/AABB: at 90° that forces a square via Math.max.
  const selectionMetrics = useMemo(() => {
    const base = activeLayout?.metrics;
    if (!base) return null;
    const pl = activeEntry?.placement;
    if (
      pl?.kind === SIGN_PDF_FIELD_KINDS.TEXT &&
      !String(pl.text ?? "").trim()
    ) {
      const fs = activeLayout.fontSizePx || 16;
      const minW = Math.max(80, Math.round(fs * 1.6));
      const minH = Math.max(24, Math.round(fs * 1.15));
      return {
        width: Math.max(base.width, minW),
        height: Math.max(base.height, minH),
      };
    }
    return base;
  }, [activeLayout, activeEntry?.placement]);

  const activeBlockId = activeEntry?.placement?.blockId || null;
  const blockEntries = useMemo(() => {
    if (!activeBlockId) return [];
    return resolvedMarks.filter(
      (m) => m.placement.blockId === activeBlockId
    );
  }, [resolvedMarks, activeBlockId]);
  const blockBounds = useMemo(
    () =>
      activeBlockId
        ? computeBlockBoundsPx(
            blockEntries,
            stageSize.width,
            stageSize.height
          )
        : null,
    [activeBlockId, blockEntries, stageSize.width, stageSize.height]
  );
  const blockMemberIds = useMemo(() => {
    if (!activeBlockId) return null;
    return new Set(blockEntries.map((m) => m.placement.id));
  }, [activeBlockId, blockEntries]);

  const patchPlacement = useCallback(
    (placementId, partial) => {
      if (!onSessionChange || !placementId) return;
      onSessionChange((prev) => {
        let s = setActiveSignPdfPlacement(prev, placementId);
        // Geometry for signature/initials goes through patchActive; for others
        // updateSignPdfPlacement is enough. Use patchActive after activate so
        // template+geo rules stay consistent for all kinds.
        return patchActiveSignPdfPlacement(s, partial, pageRef.current);
      });
    },
    [onSessionChange]
  );

  const selectId = useCallback(
    (id) => {
      if (!onSessionChange) return;
      onSessionChange((prev) => setActiveSignPdfPlacement(prev, id || null));
    },
    [onSessionChange]
  );

  const clearSelection = useCallback(() => {
    if (!onSessionChange) return;
    onSessionChange((prev) => {
      if (!prev?.activePlacementId) return prev;
      return setActiveSignPdfPlacement(prev, null);
    });
  }, [onSessionChange]);

  const commitTextEdit = useCallback(
    (textOverride) => {
      const placementId = editingTextIdRef.current;
      if (!placementId || !onSessionChange) return;
      const nextText =
        textOverride ??
        editInputRef.current?.value ??
        editDraftRef.current ??
        "";
      editingTextIdRef.current = null;
      setEditingTextId(null);
      editDraftRef.current = nextText;
      setEditDraft(nextText);
      onSessionChange((prev) =>
        updateSignPdfPlacement(prev, placementId, { text: nextText })
      );
    },
    [onSessionChange]
  );

  const commitTextEditRef = useRef(commitTextEdit);
  commitTextEditRef.current = commitTextEdit;

  const beginTextEdit = useCallback(
    (placementId, currentText = "") => {
      if (!showMarks || !onSessionChange || loading) return;
      dragRef.current = null;
      const next = currentText != null ? String(currentText) : "";
      editDraftRef.current = next;
      setEditDraft(next);
      editFocusTokenRef.current += 1;
      editingTextIdRef.current = placementId;
      setEditingTextId(placementId);
      selectId(placementId);
    },
    [showMarks, onSessionChange, loading, selectId]
  );

  const openTextEdit = useCallback(
    (e, entry) => {
      if (entry?.placement?.kind !== SIGN_PDF_FIELD_KINDS.TEXT) return;
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      dragRef.current = null;
      beginTextEdit(entry.placement.id, entry.placement.text ?? "");
    },
    [beginTextEdit]
  );

  const flashSnapGuides = useCallback((guides) => {
    if (snapGuideTimerRef.current) {
      clearTimeout(snapGuideTimerRef.current);
    }
    setSnapGuides(guides);
    if (guides) {
      snapGuideTimerRef.current = setTimeout(() => {
        setSnapGuides(null);
        snapGuideTimerRef.current = null;
      }, SNAP_GUIDE_FLASH_MS);
    }
  }, []);

  const nudgeActivePlacement = useCallback(
    (dxPx, dyPx) => {
      if (!onSessionChange || !activeId || !dxPx && !dyPx) return;
      const sw = stageSizeRef.current.width;
      const sh = stageSizeRef.current.height;
      if (!sw || !sh) return;

      const session = sessionRef.current;
      const pageNum = pageRef.current;
      const placement = listSignPdfPlacementsForPage(session, pageNum).find(
        (p) => p.id === activeId
      );
      if (!placement) return;

      const wm = resolveSignPdfPlacementToWatermark(session, placement);
      if (!wm) return;

      const layout = layoutForWatermark(wm, sw, sh);
      if (!layout?.pos || !layout.metrics) return;

      let centerX = layout.pos.x + dxPx / sw;
      let centerY = layout.pos.y + dyPx / sh;

      const synced = syncMarginFromNearestEdge({
        centerX,
        centerY,
        position: wm.position || "center",
        stageWidth: sw,
        stageHeight: sh,
        textWidth: layout.metrics.width,
        textHeight: layout.metrics.height,
        rotationDeg: Number(wm.rotation) || 0,
        flushEdges: wm.flushEdges !== false,
      });

      const blockId = placement.blockId || null;
      if (blockId) {
        const memberStarts = listSignPdfPlacementsByBlockId(session, blockId).map(
          (p) => ({
            id: p.id,
            offsetX: Number(p.offsetX) || 0,
            offsetY: Number(p.offsetY) || 0,
          })
        );
        if (memberStarts.length > 1) {
          onSessionChange((prev) =>
            patchSignPdfBlockGeometry(
              prev,
              blockId,
              memberStarts,
              activeId,
              synced
            )
          );
          return;
        }
      }

      patchPlacement(activeId, {
        offsetX: synced.offsetX,
        offsetY: synced.offsetY,
        margin: synced.margin,
      });
    },
    [activeId, onSessionChange, patchPlacement]
  );

  useEffect(() => {
    return () => {
      if (snapGuideTimerRef.current) clearTimeout(snapGuideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showMarks || !activeId || !onSessionChange) return undefined;

    const onKeyDown = (e) => {
      const focusEl = document.activeElement;
      const currentActiveId = sessionRef.current?.activePlacementId;
      if (!currentActiveId) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        if (blocksKeyboardDelete(focusEl)) return;
        e.preventDefault();
        onSessionChange((prev) =>
          removeSignPdfPlacementOrBlock(prev, currentActiveId)
        );
        setSnapGuides(null);
        return;
      }

      if (e.key === "Escape") {
        if (blocksKeyboardNudge(focusEl)) return;
        e.preventDefault();
        clearSelection();
        return;
      }

      if (blocksKeyboardNudge(focusEl)) return;

      const step = e.shiftKey ? KEY_NUDGE_SHIFT_PX : KEY_NUDGE_PX;
      let dx = 0;
      let dy = 0;
      if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else return;

      e.preventDefault();
      nudgeActivePlacement(dx, dy);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    showMarks,
    activeId,
    onSessionChange,
    clearSelection,
    nudgeActivePlacement,
  ]);

  useEffect(() => {
    if (!showMarks || !activeId) return;
    if (editingTextIdRef.current != null) return;
    stageRef.current?.focus({ preventScroll: true });
  }, [showMarks, activeId, editingTextId]);

  useEffect(() => {
    if (editingTextId == null) {
      editReadyRef.current = false;
      return undefined;
    }
    editReadyRef.current = false;
    const token = editFocusTokenRef.current;
    const id = window.requestAnimationFrame(() => {
      if (editFocusTokenRef.current !== token) return;
      const input = editInputRef.current;
      input?.focus({ preventScroll: true });
      const draft = editDraftRef.current ?? "";
      if (
        !String(draft).trim() ||
        String(draft) === SIGN_PDF_DEFAULT_TEXT_LABEL
      ) {
        input?.select?.();
      } else {
        const len = draft.length;
        input?.setSelectionRange?.(len, len);
      }
      editReadyRef.current = true;
    });
    return () => window.cancelAnimationFrame(id);
  }, [editingTextId]);

  useEffect(() => {
    if (editingTextId && activeId && editingTextId !== activeId) {
      commitTextEditRef.current();
    }
  }, [activeId, editingTextId]);

  useEffect(() => {
    if (!showMarks && editingTextIdRef.current != null) {
      commitTextEditRef.current();
    }
  }, [showMarks]);

  // Click outside a mark / selection chrome → hide handles.
  useEffect(() => {
    if (!showMarks || !activeId) return undefined;
    const onPointerDownCapture = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) {
        clearSelection();
        return;
      }
      if (
        t.closest(
          [
            ".watermark-preview__mark",
            ".watermark-preview__mark-edit-wrap",
            ".watermark-preview__mark-editor",
            ".watermark-preview__selection",
            ".sp-block-selection",
            ".watermark-preview__handle",
            ".watermark-preview__rotate",
            ".sp-sig-toolbar",
            ".sp-field-toolbar",
            ".sp-sig-panel",
            ".sp-dd",
            ".sp-dd__menu",
          ].join(",")
        )
      ) {
        return;
      }
      clearSelection();
    };
    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [showMarks, activeId, clearSelection]);

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () => {
      const aspect =
        naturalSize.width > 0 && naturalSize.height > 0
          ? naturalSize.width / naturalSize.height
          : 1;
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        aspect,
        { inset: stageInset }
      );
      if (!fitted) return;
      setStageSize(fitted);
    };
    update();
    const raf = requestAnimationFrame(update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [naturalSize, stageInset]);

  useEffect(() => {
    onStageSizeChange?.(stageSize);
  }, [onStageSizeChange, stageSize.width, stageSize.height]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height) {
      return { width: 0, height: 0, x: 0, y: 0, scale: 1 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  let canvasBg = "#111";
  if (previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL) {
    canvasBg = "#1a1a1a";
  } else if (previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT) {
    canvasBg = undefined;
  }
  const checker =
    previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT || !imageUrl;

  const endDrag = useCallback(() => {
    dragRef.current = null;
    setSnapGuides(null);
    onPlacementDragChange?.(false);
  }, [onPlacementDragChange]);

  const beginPlacementDrag = useCallback(() => {
    onPlacementDragChange?.(true);
  }, [onPlacementDragChange]);

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag || !showMarks || !drag.placementId) return;
      const placement = listSignPdfPlacementsForPage(
        sessionRef.current,
        pageRef.current
      ).find((p) => p.id === drag.placementId);
      if (!placement) return;
      const wm = resolveSignPdfPlacementToWatermark(
        sessionRef.current,
        placement
      );
      if (!wm) return;

      if (drag.type === "move-pending" || drag.type === "move") {
        const dx = e.clientX - drag.startClientX;
        const dy = e.clientY - drag.startClientY;
        if (drag.type === "move-pending") {
          if (Math.hypot(dx, dy) < 4) return;
          drag.type = "move";
          if (!drag.interactionNotified) {
            drag.interactionNotified = true;
            beginPlacementDrag();
          }
          e.preventDefault();
        }
        const metrics = drag.metrics;
        const sw = stageSizeRef.current.width;
        const sh = stageSizeRef.current.height;
        const base = resolveSingleMarkPosition({
          position: wm.position || "center",
          margin: wm.margin,
          stageWidth: sw,
          stageHeight: sh,
          textWidth: metrics.width,
          textHeight: metrics.height,
          rotationDeg: Number(wm.rotation) || 0,
          flushEdges: wm.flushEdges !== false,
        });
        let centerX = base.x + drag.startOffsetX + dx / sw;
        let centerY = base.y + drag.startOffsetY + dy / sh;

        const { halfW, halfH } = rotatedHalfExtents(
          metrics,
          Number(wm.rotation) || 0
        );
        const otherBounds = [];
        const skipIds = new Set(
          (drag.memberStarts || [{ id: drag.placementId }]).map((m) => m.id)
        );
        for (const entry of resolvedMarksRef.current) {
          if (skipIds.has(entry.placement.id)) continue;
          const layout = layoutForWatermark(entry.watermark, sw, sh);
          if (!layout?.pos || !layout.metrics) continue;
          const ext = rotatedHalfExtents(layout.metrics, layout.rotation);
          otherBounds.push(
            boundsFromCenter(
              layout.pos.x,
              layout.pos.y,
              ext.halfW,
              ext.halfH,
              sw,
              sh
            )
          );
        }
        const snapped = snapPlacementWithGuides(
          centerX,
          centerY,
          halfW,
          halfH,
          sw,
          sh,
          otherBounds
        );
        centerX = snapped.centerX;
        centerY = snapped.centerY;
        setSnapGuides(snapped.guides);

        const synced = syncMarginFromNearestEdge({
          centerX,
          centerY,
          position: wm.position || "center",
          stageWidth: sw,
          stageHeight: sh,
          textWidth: metrics.width,
          textHeight: metrics.height,
          rotationDeg: Number(wm.rotation) || 0,
          flushEdges: wm.flushEdges !== false,
        });

        if (
          drag.blockId &&
          Array.isArray(drag.memberStarts) &&
          drag.memberStarts.length > 1 &&
          onSessionChange
        ) {
          onSessionChange((prev) =>
            patchSignPdfBlockGeometry(
              prev,
              drag.blockId,
              drag.memberStarts,
              drag.placementId,
              synced
            )
          );
          return;
        }

        patchPlacement(drag.placementId, {
          offsetX: synced.offsetX,
          offsetY: synced.offsetY,
          margin: synced.margin,
        });
        return;
      }

        if (drag.type === "block-resize") {
          const rect = canvasRef.current?.getBoundingClientRect();
          const sw = stageSizeRef.current.width;
          const sh = stageSizeRef.current.height;
          if (!rect || !sw || !sh) return;
          const ax = rect.left + drag.anchorX * sw;
          const ay = rect.top + drag.anchorY * sh;
          const dist = Math.hypot(e.clientX - ax, e.clientY - ay);
          if (drag.startAnchorDist < 4) return;
          let scale = dist / drag.startAnchorDist;
          if (!Number.isFinite(scale) || scale <= 0) return;
          const snap = drag.memberSnapshot || [];
          if (!snap.length) return;
          let minS = 0.05;
          let maxS = 20;
          for (const m of snap) {
            const px = Math.max(1, Number(m.fontSizePx) || 12);
            minS = Math.max(minS, 6 / px);
            maxS = Math.min(maxS, 240 / px);
          }
          if (maxS < minS) maxS = minS;
          scale = Math.max(minS, Math.min(maxS, scale));
          onSessionChange((prev) =>
            scaleSignPdfBlockFromSnapshot(
              prev,
              drag.blockId,
              drag.placementId,
              snap,
              scale,
              drag.anchorX,
              drag.anchorY,
              sw,
              sh
            )
          );
          return;
        }

      if (drag.type === "resize") {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect || !stageSize.width || !stageSize.height) return;
        const ax = rect.left + drag.anchorX * stageSize.width;
        const ay = rect.top + drag.anchorY * stageSize.height;
        const isEdge = drag.corner === "w" || drag.corner === "e";
        let scale;
        if (isEdge) {
          const dx = e.clientX - ax;
          const startDx = drag.startClientX - ax;
          if (Math.abs(startDx) < 4) return;
          scale = dx / startDx;
        } else {
          const dist = Math.hypot(e.clientX - ax, e.clientY - ay);
          if (drag.startAnchorDist < 4) return;
          scale = dist / drag.startAnchorDist;
        }
        if (!Number.isFinite(scale) || scale <= 0) return;
        const startPx = Math.max(
          1,
          Number(drag.startFontSizePx) || Number(wm.fontSizePx) || 12
        );
        const nextPx = Math.max(6, Math.min(240, startPx * scale));
        const appliedScale = nextPx / startPx;
        const newHalfW = drag.startHalfW * appliedScale;
        const newHalfH = drag.startHalfH * appliedScale;
        const newCenterX =
          drag.anchorX + (drag.signX || 0) * (newHalfW / stageSize.width);
        const newCenterY = isEdge
          ? drag.centerY
          : drag.anchorY + (drag.signY || 0) * (newHalfH / stageSize.height);
        const metrics = {
          width: Math.max(1, newHalfW * 2),
          height: Math.max(1, newHalfH * 2),
        };
        const base = resolveSingleMarkPosition({
          position: wm.position || "center",
          margin: wm.margin,
          stageWidth: stageSize.width,
          stageHeight: stageSize.height,
          textWidth: metrics.width,
          textHeight: metrics.height,
          rotationDeg: Number(wm.rotation) || 0,
          flushEdges: wm.flushEdges !== false,
        });
        patchPlacement(drag.placementId, {
          ...signPdfFontSizeFieldsFromPx(nextPx, stageSize.width, stageSize.height, {
            logo: isLogoWatermark(wm),
          }),
          offsetX: newCenterX - base.x,
          offsetY: newCenterY - base.y,
        });
        return;
      }

      if (drag.type === "rotate") {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const cx = rect.left + drag.centerX * stageSize.width;
        const cy = rect.top + drag.centerY * stageSize.height;
        const angle =
          (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
        let next = drag.startRotation + (angle - drag.startAngle);
        next = ((next + 180) % 360 + 360) % 360 - 180;
        patchPlacement(
          drag.placementId,
          watermarkRotationPreservingCenter({
            nextRotation: Math.round(next),
            centerX: drag.centerX,
            centerY: drag.centerY,
            position: wm.position || "center",
            margin: wm.margin,
            stageWidth: stageSize.width,
            stageHeight: stageSize.height,
            textWidth: drag.metrics.width,
            textHeight: drag.metrics.height,
            flushEdges: wm.flushEdges !== false,
          })
        );
      }
    };

    const onUp = () => endDrag();
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [showMarks, stageSize, patchPlacement, endDrag, onSessionChange, beginPlacementDrag]);

  const startMove = (e, entry, layout) => {
    if (!showMarks || !onSessionChange || loading) return;
    const isTextField = entry.placement.kind === SIGN_PDF_FIELD_KINDS.TEXT;
    if (isTextField && e.detail >= 2) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (editingTextIdRef.current != null) {
      if (editingTextIdRef.current === entry.placement.id && isTextField) {
        return;
      }
      commitTextEditRef.current();
    }
    e.preventDefault();
    e.stopPropagation();
    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
    selectId(entry.placement.id);
    stageRef.current?.focus({ preventScroll: true });
    const blockId = entry.placement.blockId || null;
    const memberStarts = blockId
      ? listSignPdfPlacementsByBlockId(sessionRef.current, blockId).map((p) => ({
          id: p.id,
          offsetX: Number(p.offsetX) || 0,
          offsetY: Number(p.offsetY) || 0,
        }))
      : null;
    dragRef.current = {
      type: "move-pending",
      placementId: entry.placement.id,
      blockId,
      memberStarts,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startOffsetX: Number(entry.placement.offsetX) || 0,
      startOffsetY: Number(entry.placement.offsetY) || 0,
      metrics: layout.metrics,
    };
  };

  const startBlockResize = (e, corner) => {
    if (
      !showMarks ||
      !onSessionChange ||
      !activeBlockId ||
      !blockBounds ||
      !activeEntry ||
      loading
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    const sw = stageSize.width;
    const sh = stageSize.height;
    if (!rect || !sw || !sh) return;

    const centerX = (blockBounds.left + blockBounds.width / 2) / sw;
    const centerY = (blockBounds.top + blockBounds.height / 2) / sh;
    const halfWNorm = blockBounds.width / 2 / sw;
    const halfHNorm = blockBounds.height / 2 / sh;
    let signX = 0;
    let signY = 0;
    if (corner.includes("e")) signX = 1;
    if (corner.includes("w")) signX = -1;
    if (corner.includes("s")) signY = 1;
    if (corner.includes("n")) signY = -1;
    const anchorX = centerX - signX * halfWNorm;
    const anchorY = centerY - signY * halfHNorm;
    const ax = rect.left + anchorX * sw;
    const ay = rect.top + anchorY * sh;
    const memberSnapshot = snapshotSignPdfBlockForResize(
      sessionRef.current,
      activeBlockId,
      sw,
      sh
    );
    if (!memberSnapshot.length) return;
    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
    dragRef.current = {
      type: "block-resize",
      blockId: activeBlockId,
      placementId: activeEntry.placement.id,
      corner,
      signX,
      signY,
      anchorX,
      anchorY,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startAnchorDist: Math.max(Math.hypot(e.clientX - ax, e.clientY - ay), 8),
      memberSnapshot,
      interactionNotified: true,
    };
    beginPlacementDrag();
  };

  const startResize = (e, corner) => {
    if (
      !showMarks ||
      !onSessionChange ||
      !activeLayout ||
      !activeEntry ||
      !selectionMetrics ||
      loading
    )
      return;
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !stageSize.width || !stageSize.height) return;
    const { pos } = activeLayout;
    const metrics = selectionMetrics;
    const halfW = Math.max(1, metrics.width / 2);
    const halfH = Math.max(1, metrics.height / 2);
    const halfWNorm = halfW / stageSize.width;
    const halfHNorm = halfH / stageSize.height;
    let signX = 0;
    let signY = 0;
    if (corner.includes("e")) signX = 1;
    if (corner.includes("w")) signX = -1;
    if (corner.includes("s")) signY = 1;
    if (corner.includes("n")) signY = -1;
    const anchorX = pos.x - signX * halfWNorm;
    const anchorY = signY === 0 ? pos.y : pos.y - signY * halfHNorm;
    const ax = rect.left + anchorX * stageSize.width;
    const ay = rect.top + anchorY * stageSize.height;
    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
    dragRef.current = {
      type: "resize",
      placementId: activeEntry.placement.id,
      corner,
      signX,
      signY,
      anchorX,
      anchorY,
      centerX: pos.x,
      centerY: pos.y,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startAnchorDist: Math.max(Math.hypot(e.clientX - ax, e.clientY - ay), 8),
      startSize: Math.max(0.01, Number(activeWm?.size) || 1.5),
      startFontSizePx: Math.max(
        1,
        Number(activeWm?.fontSizePx) ||
          Number(activeLayout?.fontSizePx) ||
          12
      ),
      startHalfW: halfW,
      startHalfH: halfH,
      metrics,
      interactionNotified: true,
    };
    beginPlacementDrag();
  };

  const startRotate = (e) => {
    if (
      !showMarks ||
      !onSessionChange ||
      !activeLayout ||
      !activeEntry ||
      !selectionMetrics ||
      loading
    )
      return;
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const { pos } = activeLayout;
    const metrics = selectionMetrics;
    const cx = rect.left + pos.x * stageSize.width;
    const cy = rect.top + pos.y * stageSize.height;
    const startAngle =
      (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
    dragRef.current = {
      type: "rotate",
      placementId: activeEntry.placement.id,
      centerX: pos.x,
      centerY: pos.y,
      startAngle,
      startRotation: Number(activeWm?.rotation) || 0,
      metrics,
      interactionNotified: true,
    };
    beginPlacementDrag();
  };

  return (
    <ToolPrepCanvasStage
      className={`transform-preview watermark-preview sp-preview-canvas${
        imageUrl ? " has-source" : " is-empty"
      }`}
      ref={stageRef}
      metaImageUrl={imageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
        tabIndex={showMarks && activeId ? 0 : -1}
        role={showMarks && activeId ? "application" : undefined}
        aria-label={
          showMarks && activeId
            ? "Document field selected. Use arrow keys to nudge, Delete to remove."
            : undefined
        }
        onDragOver={
        onFieldDrop || onSigningBlockDrop
          ? (e) => {
              const types = Array.from(e.dataTransfer?.types || []);
              const hasField =
                e.dataTransfer?.types?.includes?.(
                  "application/x-sign-pdf-field"
                ) || types.includes("application/x-sign-pdf-field");
              const hasBlock =
                e.dataTransfer?.types?.includes?.(SIGN_PDF_BLOCK_MIME) ||
                types.includes(SIGN_PDF_BLOCK_MIME);
              if ((hasField && onFieldDrop) || (hasBlock && onSigningBlockDrop)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
              }
            }
          : undefined
      }
      onDrop={
        onFieldDrop || onSigningBlockDrop
          ? (e) => {
              e.preventDefault();
              const blockPreset = e.dataTransfer.getData(SIGN_PDF_BLOCK_MIME);
              if (blockPreset && onSigningBlockDrop) {
                onSigningBlockDrop(blockPreset, e);
                return;
              }
              const kind = e.dataTransfer.getData(
                "application/x-sign-pdf-field"
              );
              if (kind && onFieldDrop) onFieldDrop(kind, e);
            }
          : undefined
      }
    >
      <div
        ref={canvasRef}
        className={`transform-preview__canvas${
          checker ? " transform-preview__canvas--checker" : ""
        }`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
          backgroundColor: checker ? undefined : canvasBg,
        }}
        onPointerDown={(e) => {
          // Empty canvas / page chrome — deselect so the stamp is fully visible.
          if (e.target === e.currentTarget) {
            clearSelection();
          }
        }}
      >
        {loading ? (
          <div className="transform-preview__loading">
            <RoundLoader size="md" label={loadingLabel} />
          </div>
        ) : imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt="Sign PDF preview"
              className={
                transform.width > 0
                  ? "transform-preview__image"
                  : "transform-preview__image transform-preview__image--contain"
              }
              style={
                transform.width > 0
                  ? {
                      width: transform.width,
                      height: transform.height,
                      left: transform.x,
                      top: transform.y,
                    }
                  : undefined
              }
              draggable={false}
              onPointerDown={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
            />
            {showMarks ? (
              <div
                className="watermark-preview__overlay sp-preview-canvas__overlay"
                onPointerDown={(e) => {
                  // Click empty overlay (not a mark) clears selection.
                  if (e.target === e.currentTarget) {
                    clearSelection();
                  }
                }}
              >
                {resolvedMarks.map((entry) => {
                  const layout = layoutForWatermark(
                    entry.watermark,
                    stageSize.width,
                    stageSize.height
                  );
                  if (!layout) return null;
                  const { pos, metrics, logo, rotation, fontFamily, fontSizePx } =
                    layout;
                  const wm = entry.watermark;
                  const isActive = Boolean(activeId) && entry.placement.id === activeId;
                  const inBlock =
                    Boolean(blockMemberIds) &&
                    blockMemberIds.has(entry.placement.id);
                  const opacity =
                    Math.max(0, Math.min(100, Number(wm.opacity) || 0)) / 100;
                  const sharedStyle = {
                    left: `${pos.x * 100}%`,
                    top: `${pos.y * 100}%`,
                    opacity,
                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                    zIndex: isActive || inBlock ? 5 : 3,
                  };

                  if (logo) {
                    const { style: logoStyle, tint, imgStyle, effect, effectColor } =
                      watermarkLogoMarkStyle(
                        wm,
                        `translate(-50%, -50%) rotate(${rotation}deg)`
                      );
                    return (
                      <button
                        key={entry.placement.id}
                        type="button"
                        className={`watermark-preview__mark watermark-preview__mark--logo${
                          isActive ? " is-selected" : ""
                        }${inBlock ? " is-block-member" : ""}`}
                        style={{
                          ...sharedStyle,
                          ...logoStyle,
                          left: `${pos.x * 100}%`,
                          top: `${pos.y * 100}%`,
                          width: metrics.width,
                          height: metrics.height,
                          padding: 0,
                          border: "none",
                          background: "transparent",
                        }}
                        aria-label={
                          inBlock
                            ? isActive
                              ? "Selected signing block"
                              : "Signing block member"
                            : isActive
                              ? "Selected signature mark"
                              : "Select signature mark"
                        }
                        onPointerDown={(e) => startMove(e, entry, layout)}
                      >
                        <WatermarkLogoMarkMedia
                          logoUrl={wm.logoUrl}
                          tint={tint}
                          effect={effect}
                          effectColor={effectColor}
                          imgStyle={imgStyle}
                        />
                      </button>
                    );
                  }

                  const isTextField =
                    entry.placement.kind === SIGN_PDF_FIELD_KINDS.TEXT;
                  const isEditingThis =
                    isTextField && editingTextId === entry.placement.id;
                  const placementText =
                    entry.placement.text != null
                      ? String(entry.placement.text)
                      : "";
                  const displayText = isTextField
                    ? placementText.trim()
                    : wm.text?.trim()
                      ? wm.text
                      : "Text";
                  const isEmptyText = isTextField && !displayText;
                  const emptyBoxMinW = Math.max(80, Math.round(fontSizePx * 1.6));
                  const emptyBoxMinH = Math.max(24, Math.round(fontSizePx * 1.15));
                  const textAlign = wm.textAlign || "center";
                  const editorDraftText =
                    isEditingThis && String(editDraft ?? "").length > 0
                      ? String(editDraft)
                      : null;
                  const editorMetrics =
                    editorDraftText != null
                      ? measureSignPdfText(
                          editorDraftText,
                          fontSizePx,
                          fontFamily,
                          wm.fontWeight,
                          {
                            letterSpacing: wm.letterSpacing,
                            fontStyle: wm.fontStyle,
                          }
                        )
                      : metrics;
                  const boxMetrics =
                    isActive && selectionMetrics && !activeBlockId && !isEditingThis
                      ? selectionMetrics
                      : isEditingThis
                        ? editorMetrics
                        : metrics;
                  const textStyle = {
                    color: wm.color || "#111111",
                    fontFamily,
                    fontSize: fontSizePx,
                    fontWeight: wm.fontWeight === "bold" ? 700 : 400,
                    fontStyle: watermarkFontCssStyle(wm.fontStyle),
                    textDecoration: watermarkTextDecoration(wm),
                    ...watermarkEffectStyle(wm.effect || "none", wm.color || "#111"),
                    ...watermarkTextAdvancedCss(wm, fontSizePx),
                    ...watermarkTextBoxLayout(textAlign, boxMetrics, {
                      boxWidth: undefined,
                      extraWidth: 0,
                      textScript: wm.textScript || "normal",
                    }),
                    width: isEmptyText
                      ? Math.max(boxMetrics.width, emptyBoxMinW)
                      : boxMetrics.width,
                    height: isEmptyText
                      ? Math.max(boxMetrics.height, emptyBoxMinH)
                      : boxMetrics.height,
                    lineHeight: `${
                      isEmptyText
                        ? Math.max(boxMetrics.height, emptyBoxMinH)
                        : boxMetrics.height
                    }px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                    boxSizing: "border-box",
                  };

                  if (isEditingThis) {
                    const editPadX = 20;
                    const editW = Math.max(
                      emptyBoxMinW,
                      Math.ceil(editorMetrics.width + editPadX)
                    );
                    const editH = Math.max(
                      emptyBoxMinH,
                      Math.ceil(editorMetrics.height)
                    );
                    return (
                      <div
                        key={entry.placement.id}
                        className="watermark-preview__mark-edit-wrap"
                        style={{
                          ...sharedStyle,
                          zIndex: 10,
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                      >
                        <span className="sp-text-edit-badge" aria-hidden="true">
                          Editing
                        </span>
                        <input
                          ref={editInputRef}
                          type="text"
                          className="watermark-preview__mark-editor"
                          value={editDraft}
                          placeholder="Type here…"
                          spellCheck={false}
                          autoComplete="off"
                          aria-label="Edit text field"
                          onChange={(e) => {
                            editDraftRef.current = e.target.value;
                            setEditDraft(e.target.value);
                          }}
                          onBlur={() => {
                            if (!editReadyRef.current) {
                              window.setTimeout(() => {
                                editInputRef.current?.focus({
                                  preventScroll: true,
                                });
                              }, 0);
                              return;
                            }
                            window.setTimeout(() => {
                              if (
                                editInputRef.current === document.activeElement
                              ) {
                                return;
                              }
                              commitTextEditRef.current();
                            }, 0);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Escape") {
                              e.preventDefault();
                              commitTextEditRef.current();
                              return;
                            }
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitTextEditRef.current();
                              return;
                            }
                            if (e.key === "Delete" || e.key === "Backspace") {
                              const input = editInputRef.current;
                              const draft = String(editDraftRef.current ?? "");
                              const len = draft.length;
                              const selStart = input?.selectionStart ?? 0;
                              const selEnd = input?.selectionEnd ?? 0;
                              const allSelected =
                                len > 0 && selStart === 0 && selEnd === len;
                              const isEmpty = !draft.trim();
                              if (isEmpty || allSelected) {
                                e.preventDefault();
                                const id = editingTextIdRef.current;
                                editingTextIdRef.current = null;
                                setEditingTextId(null);
                                if (id && onSessionChange) {
                                  onSessionChange((prev) =>
                                    removeSignPdfPlacementOrBlock(prev, id)
                                  );
                                }
                              }
                              return;
                            }
                          }}
                          style={{
                            color: wm.color || "#111111",
                            fontFamily,
                            fontSize: fontSizePx,
                            fontWeight:
                              wm.fontWeight === "bold" ? 700 : 400,
                            fontStyle: watermarkFontCssStyle(wm.fontStyle),
                            textDecoration: watermarkTextDecoration(wm),
                            ...watermarkTextAdvancedCss(wm, fontSizePx),
                            position: "relative",
                            left: "auto",
                            top: "auto",
                            transform: "none",
                            opacity: 1,
                            width: editW,
                            height: editH,
                            minWidth: emptyBoxMinW,
                            minHeight: emptyBoxMinH,
                            padding: "2px 8px",
                            margin: 0,
                            textAlign: "left",
                            lineHeight: `${editH}px`,
                          }}
                        />
                      </div>
                    );
                  }

                  return (
                    <button
                      key={entry.placement.id}
                      type="button"
                      className={`watermark-preview__mark${
                        isActive ? " is-selected" : ""
                      }${inBlock ? " is-block-member" : ""}${
                        isEmptyText ? " is-empty-text" : ""
                      }${isEmptyText && !isActive ? " is-empty-text--idle" : ""}`}
                      style={{
                        ...sharedStyle,
                        ...textStyle,
                        padding: 0,
                        border: "none",
                        background: "transparent",
                      }}
                      aria-label={
                        inBlock
                          ? isActive
                            ? "Selected signing block"
                            : "Signing block member"
                          : isActive
                            ? isTextField
                              ? "Selected text field — double-click to edit"
                              : "Selected field"
                            : isTextField
                              ? "Select text field — double-click to edit"
                              : "Select field"
                      }
                      onPointerDown={(e) => startMove(e, entry, layout)}
                      onDoubleClick={
                        isTextField ? (e) => openTextEdit(e, entry) : undefined
                      }
                    >
                      {displayText ||
                        (isEmptyText && isActive ? "Double-click to edit" : "")}
                    </button>
                  );
                })}

                {snapGuides ? (
                  <div className="sp-snap-guides" aria-hidden="true">
                    {snapGuides.v != null ? (
                      <span
                        className="sp-snap-guides__v"
                        style={{ left: `${snapGuides.v * 100}%` }}
                      />
                    ) : null}
                    {snapGuides.h != null ? (
                      <span
                        className="sp-snap-guides__h"
                        style={{ top: `${snapGuides.h * 100}%` }}
                      />
                    ) : null}
                  </div>
                ) : null}

                {activeBlockId && blockBounds ? (
                  <div
                    className="sp-block-selection"
                    style={{
                      left: blockBounds.left,
                      top: blockBounds.top,
                      width: blockBounds.width,
                      height: blockBounds.height,
                    }}
                  >
                    {HANDLES.map((h) => (
                      <span
                        key={h}
                        className={`watermark-preview__handle watermark-preview__handle--${h}`}
                        onPointerDown={(e) => startBlockResize(e, h)}
                        aria-label={`Resize signing block ${h}`}
                      />
                    ))}
                  </div>
                ) : null}

                {!activeBlockId &&
                !textEditing &&
                activeId &&
                activeLayout &&
                activeEntry &&
                selectionMetrics ? (
                  <div
                    className="watermark-preview__selection"
                    style={{
                      left: `${activeLayout.pos.x * 100}%`,
                      top: `${activeLayout.pos.y * 100}%`,
                      width: selectionMetrics.width,
                      height: selectionMetrics.height,
                      transform: `translate(-50%, -50%) rotate(${activeLayout.rotation}deg)`,
                    }}
                  >
                    {HANDLES.map((h) => (
                      <span
                        key={h}
                        className={`watermark-preview__handle watermark-preview__handle--${h}`}
                        onPointerDown={(e) => startResize(e, h)}
                      />
                    ))}
                    <div className="sp-preview-rotate-cluster">
                      <button
                        type="button"
                        className="watermark-preview__rotate"
                        aria-label="Rotate"
                        title="Rotate"
                        onPointerDown={startRotate}
                      >
                        <svg
                          className="watermark-preview__rotate-icon"
                          viewBox="0 0 16 16"
                          aria-hidden="true"
                        >
                          <path
                            d="M13.5 8A5.5 5.5 0 1 1 8 2.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8 1v3.2L10.6 2.6 8 1Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                      <span
                        className="sp-preview-rotate-angle"
                        aria-live="polite"
                      >
                        {Math.round(Number(activeLayout.rotation) || 0)}°
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <div className="transform-preview__empty">{emptyLabel}</div>
        )}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default SignPdfPreview;
